import { PartialExcept } from './CommonTypes'
import { CarpoolType, DateCarpoolProps, DateTimeProps } from './CommunityTypes'
import { StudentProps } from './StudentTypes'
import { UserOutputProps, UsersOutputProps } from './UserTypes'

/**
 * Enum for different calendar event types.
 */
export enum CalendarType {
  CARPOOL_REQUEST = 'carpool-request',
  CARPOOL = 'carpool',
}

/*
 * Enum, types, and utilities for different calendar states.
 */
export enum CarpoolRequestState {
  SEEKING_CARPOOL = 'Seeking carpool',
  IN_A_CARPOOL = 'In a carpool',
  NOT_CARPOOLING = 'Not carpooling',
}

export enum CarpoolState {
  YOU_ARE_DRIVING = "You're driving",
  SOMEONE_ELSE_DRIVING = 'Someone else driving',
  NOT_CARPOOLING = 'Not carpooling',
}
export type CalendarState = CarpoolRequestState | CarpoolState
export const getInitialSelectedDatesState = (): Record<CalendarState, string[]> => {
  const initialState = {} as Record<CalendarState, string[]>
  Object.values({ ...CarpoolRequestState, ...CarpoolState }).forEach((state) => {
    initialState[state as CalendarState] = []
  })
  return initialState
}
export const getCarpoolDriverSelectedDates = (
  selectedDatesByState: Record<CalendarState, string[]>,
): Record<CarpoolState, string[]> => {
  // Start with all driver states initialized to empty arrays
  const result = getInitialSelectedDatesState()

  // Only keep the CarpoolState entries
  Object.entries(selectedDatesByState).forEach(([state, dates]) => {
    if (Object.values(CarpoolState).includes(state as CarpoolState)) {
      result[state as CarpoolState] = dates
    }
  })

  return result
}

/*
 * Enum, types, and utilities for different calendar callbacks.
 */
export type CalendarCallback = DateTimeProps

/**
 * NOTE: The eventData and eventOptions fields are intentionally loosely typed (Record<string, unknown>).
 * Each calendar event type can define its own structure for these fields.
 *
 * Current calendar types and their definitions:
 *
 * 1. CARPOOL_REQUEST
 *    - eventData: { isCarpoolRequest: boolean, carpoolTime: number } // Record of carpool times for selected dates
 *
 * 2. CARPOOL
 *    - eventData: { isCarpoolRequest: boolean, carpoolTime: number, carpoolDriver?: { userId: string, userName: string, isCurrentUser?: boolean } } // Record of pickup times and driver ID for selected dates
 *
 * When adding new calendar types, define the structure of these fields as appropriate for your use case.
 */

export type CalendarInputProps = {
  eventDate: string // ISO date string for the event
  eventData: Record<string, unknown> | null // Arbitrary event-specific rendered data
}
export type CalendarGridProps = PartialExcept<CalendarInputProps, 'eventDate'>
export interface CarpoolRequestOptionProps {
  communityTime: number
}
export interface CarpoolOptionProps {
  studentId: string
  user: UserOutputProps
  userStudents: StudentProps[]
  poolType: CarpoolType
  communityUsers: UsersOutputProps[]
}

/**
 * Main props for the calendar component.
 */
export interface CalendarProps {
  type: CalendarType // Type of the calendar
  events: CalendarInputProps[] // List of calendar events to display
  carpoolRequestOptions?: CarpoolRequestOptionProps
  carpoolOptions?: CarpoolOptionProps
  onCarpoolRequest?: (body: DateTimeProps) => Promise<void>
  onCarpool?: (body: DateCarpoolProps) => Promise<void>
  showDate?: Date // Date to display in the calendar view
  showWeekends?: boolean // Whether to show weekends in the calendar
  isReadOnly?: boolean
}

/**
 * Type guard to check if a value is an object with a numeric time property
 */
function hasTimeProperty(data: unknown, timeKey: string): data is Record<string, number> {
  if (!data || typeof data !== 'object') return false
  const time = (data as Record<string, unknown>)[timeKey]
  return typeof time === 'number'
}

/**
 * Type guard to check if a value is an object with a driver property
 */
function hasDriverProperty(
  data: unknown,
  driverKey: string,
): data is Record<string, { userId: string; userName: string }> {
  if (!data || typeof data !== 'object') return false

  const driver = (data as Record<string, unknown>)[driverKey]
  if (!driver || typeof driver !== 'object') return false

  const { userId, userName, isCurrentUser } = driver as { userId: unknown; userName: unknown; isCurrentUser?: unknown }
  return (
    typeof userId === 'string' &&
    typeof userName === 'string' &&
    (isCurrentUser === undefined || typeof isCurrentUser === 'boolean')
  )
}

/**
 * Type guard to check if a value is a valid carpool time
 */
export function isCalendarTypeCarpoolRequestEventData(
  data: unknown,
): data is { carpoolTime: number; isCarpoolRequest: boolean } {
  return hasTimeProperty(data, 'carpoolTime') && typeof data?.isCarpoolRequest === 'boolean'
}

/**
 * Type guard to check if a value is a valid designate driver
 */
export interface CarpoolEventData {
  isCarpoolRequest: boolean
  carpoolTime: number
  carpoolDriver?: {
    userId: string
    userName: string
    isCurrentUser?: boolean
  }
}

export function isCalendarTypeCarpoolEventData(data: unknown): data is CarpoolEventData {
  return (
    hasTimeProperty(data, 'carpoolTime') &&
    typeof data?.isCarpoolRequest === 'boolean' &&
    (hasDriverProperty(data, 'carpoolDriver') || !data?.carpoolDriver)
  )
}
